/**
 * The two fields of the profile the backend keeps as codes rather than text —
 * gender and the specializations — and the words the form puts in front of
 * them.
 *
 * `/api/v1/settings/` sends and takes `FEMALE` and `WEIGHT_MANAGEMENT`; the
 * dropdown and the chips say "Female" and "Weight management". The translation
 * happens here and nowhere else, so the wording is a display concern the way it
 * should be — renaming a chip is an edit to one line in this file and changes
 * nothing that goes over the wire.
 *
 * Both maps are written code-first, and the option lists the form offers are
 * derived from them: a code with no label, or a label the backend has never
 * heard of, is then impossible to introduce by editing one list and forgetting
 * the other.
 *
 * Neither map is treated as closed. A code that arrives and is not listed is
 * shown as its own words rather than dropped — a profile must not lose a
 * specialization because this app is a release behind the backend's enum — and
 * a label typed into the multi-select that no code matches is sent as one
 * derived from it. Which is the honest handling of both, but it does mean the
 * lists below are worth checking against the backend's own when either changes.
 */

/** `MALE` ⇄ "Male". The three the backend keeps, in the order the form offers. */
const GENDER_LABELS: Record<string, string> = {
  MALE: "Male",
  FEMALE: "Female",
  OTHER: "Other",
};

/**
 * `WEIGHT_MANAGEMENT` ⇄ "Weight management".
 *
 * Four of these are confirmed against the API — `WEIGHT_MANAGEMENT`,
 * `DIABETES_NUTRITION`, `GUT_HEALTH` and `PCOS_NUTRITION`, which is what a
 * saved profile comes back holding. The rest are the labels this screen has
 * always offered, coded the same way the confirmed four are. If the backend
 * spells one of them differently it will refuse the save and name the field, so
 * a wrong guess here surfaces as a message rather than as a silent loss.
 */
const SPECIALIZATION_LABELS: Record<string, string> = {
  WEIGHT_MANAGEMENT: "Weight management",
  DIABETES_NUTRITION: "Diabetic nutrition",
  RENAL_HEALTH: "Renal health",
  SPORTS_NUTRITION: "Sports nutrition",
  GUT_HEALTH: "Gut health",
  PCOS_NUTRITION: "PCOS",
  THYROID_CARE: "Thyroid care",
  CHILD_NUTRITION: "Child nutrition",
  PREGNANCY_NUTRITION: "Pregnancy nutrition",
  CARDIAC_NUTRITION: "Cardiac nutrition",
  CLINICAL_NUTRITION: "Clinical nutrition",
  VEGAN_NUTRITION: "Vegan nutrition",
};

/** The reverse of a code→label map, matched on lower-cased text so the case a
 *  label is typed or stored in never decides whether it is recognised. */
const codesByLabel = (labels: Record<string, string>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(labels).map(([code, label]) => [label.toLowerCase(), code])
  );

const GENDER_CODES = codesByLabel(GENDER_LABELS);
const SPECIALIZATION_CODES = codesByLabel(SPECIALIZATION_LABELS);

/**
 * A code the map does not hold, said as words: `RENAL_HEALTH` → "Renal health".
 * What an unlisted value is shown as, so it reads like the ones beside it
 * rather than shouting in the backend's own casing.
 */
const labelFromCode = (code: string): string => {
  const words = code.trim().replace(/_+/g, " ").toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
};

/**
 * A label the map does not hold, said as a code: "Renal health" →
 * `RENAL_HEALTH`. The shape every code in the maps above has, which is the best
 * guess available for one that is not in them.
 */
const codeFromLabel = (label: string): string =>
  label
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/** What the Gender dropdown offers, in the backend's own order. */
export const GENDER_OPTIONS = Object.values(GENDER_LABELS);

/**
 * What the Specializations multi-select suggests. A suggestion rather than a
 * closed set — the control keeps a typed entry the list has never heard of,
 * and `toSpecializationCodes` codes it rather than dropping it.
 */
export const SPECIALIZATION_OPTIONS = Object.values(SPECIALIZATION_LABELS);

/** `"FEMALE"` → `"Female"`. An empty or missing value stays empty. */
export const toGenderLabel = (code: string | null | undefined): string =>
  code ? GENDER_LABELS[code] ?? labelFromCode(code) : "";

/** `"Female"` → `"FEMALE"`. An empty label stays empty. */
export const toGenderCode = (label: string): string => {
  const value = label.trim();
  if (!value) return "";
  return GENDER_CODES[value.toLowerCase()] ?? codeFromLabel(value);
};

/** The codes a profile came back holding, as the chips the card draws. */
export const toSpecializationLabels = (
  codes: string[] | null | undefined
): string[] =>
  (codes ?? [])
    .map((code) => SPECIALIZATION_LABELS[code] ?? labelFromCode(code))
    .filter(Boolean);

/** The chips as the codes a save sends. */
export const toSpecializationCodes = (labels: string[]): string[] =>
  labels
    .map((label) => {
      const value = label.trim();
      if (!value) return "";
      return SPECIALIZATION_CODES[value.toLowerCase()] ?? codeFromLabel(value);
    })
    .filter(Boolean);
